import urls from "global/constants/UrlConstants";
import {
  InPersonHostLinkResponse,
  InPersonSessionResponse,
  InPersonSessionStatus,
} from "models/interfaces";
import { getCallParams, getNoAuthCallParams, makeCall } from "utils/service";

/**
 * Host-authenticated in-person session endpoints. These run in the sender's
 * logged-in session (normal auth) and are keyed by the envelope / sign-request
 * `requestId`. The signing itself reuses the existing token-scoped endpoints
 * against the `requestToken` these return — see the PRD hand-off spec.
 */

/**
 * Options for start / next:
 * - `signerEmail` (query): selects a specific already-emailed signer when
 *   several are eligible at the same order (parallel envelopes). Omit to
 *   auto-pick the lowest-order actionable signer — the only case for sequential
 *   envelopes. Targeting a non-actionable signer returns 406.
 * - `name` / `email` (body): captured by the host at hand-off for a blank
 *   placeholder signer. If the placeholder still has no name, the backend
 *   returns 406 asking the host to provide it. `email` is optional — an
 *   in-person signer needs no email to sign, only to receive their copy — and
 *   is omitted from the body when the host left it empty.
 */
export interface InPersonSessionOptions {
  signerEmail?: string;
  name?: string;
  email?: string;
}

/** Envelope id path segment — encoded for the same reason `signerEmail` is. */
const sessionUrl = (base: string, requestId: string) =>
  `${base}/${encodeURIComponent(requestId)}`;

const withSignerEmail = (base: string, signerEmail?: string) =>
  signerEmail
    ? `${base}?signerEmail=${encodeURIComponent(signerEmail)}`
    : base;

/**
 * Only send the capture fields the host actually entered. A walk-in signer's
 * email is optional, so a blank one is dropped rather than posted as "".
 */
const captureBody = (options?: InPersonSessionOptions) => {
  const body: { name?: string; email?: string } = {};
  if (options?.name) body.name = options.name;
  if (options?.email) body.email = options.email;
  return body;
};

/** Consent status for the signing surface, keyed by the signer's token. */
export interface InPersonSignerConsentStatus {
  /** `true` for a remote signer too, so this alone answers "gate or not?". */
  signerConsented?: boolean;
  signerRef?: string;
  signerAgreementId?: string;
}

/**
 * The consent record the backend writes carries the acceptor, their IP and
 * user agent, all resolved server-side — a consent the client could dictate
 * would be evidence of nothing. So the body is only ever `{accepted: true}`;
 * posting `false` is a 406 and writes nothing.
 */
const CONSENT_ACCEPTED = { accepted: true };

/**
 * The host attests that the signer is physically in front of them. Required
 * before `start` / `next`, which 406 without it.
 *
 * Posted on **every** session start, never read back first: the attestation is
 * about the hand-off happening right now, so an earlier one is not an answer to
 * it. That is also why there is no status read here — consulting it would only
 * ever be used to skip the question.
 */
export const recordHostConsent = async (requestId: string): Promise<void> => {
  try {
    const url = sessionUrl(urls.IN_PERSON_CONSENT_HOST, requestId);
    const callParams = await getCallParams("POST", CONSENT_ACCEPTED);
    await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

/** Account-level, not tied to any envelope. */
export interface RequesterConsentStatus {
  requesterConsented: boolean;
  requesterAgreementId?: string;
}

/**
 * Read once per account — whether the requester has ever attested to using
 * in-person signing, so the compose-time prompt is only asked the first time
 * they add an in-person signer, not on every signer / every envelope.
 */
export const fetchRequesterConsent = async (): Promise<RequesterConsentStatus> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.IN_PERSON_REQUESTER_CONSENT, callParams);
    return response ?? { requesterConsented: false };
  } catch (error: any) {
    throw error;
  }
};

/** The requester's own compose-time attestation — posted once, ever, per account. */
export const recordRequesterConsent = async (): Promise<void> => {
  try {
    const callParams = await getCallParams("POST", CONSENT_ACCEPTED);
    await makeCall(urls.IN_PERSON_REQUESTER_CONSENT, callParams);
  } catch (error: any) {
    throw error;
  }
};

/**
 * Token-scoped (`callback/`) — the walk-up signer holding the host's device
 * has no session of their own, so these authenticate with the `requestToken`
 * the hand-off returned, exactly like the rest of the signing surface. Both
 * `id` and `key` are required: CryptoService's signed-token check binds the
 * key to `id`, so a call carrying `key` alone (the old shared-secret shape)
 * 401s regardless of the key's own validity.
 */
const signerConsentUrl = (requestToken: string, requestKey: string) =>
  `${urls.IN_PERSON_CONSENT_SIGNER}/${encodeURIComponent(
    requestToken,
  )}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(requestKey)}`;

/** Read on load of the signing surface — decides whether to gate the fields. */
export const fetchSignerConsent = async (
  requestToken: string,
  requestKey: string,
): Promise<InPersonSignerConsentStatus> => {
  try {
    const callParams = getNoAuthCallParams("GET");
    const response = await makeCall(
      signerConsentUrl(requestToken, requestKey),
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Attach an email to the just-finished signer so the completed copy reaches
 * them. Offered once, on the completion card, while the signer still has the
 * device — after that the envelope has no way to reach them directly.
 *
 * Token-scoped like the consent calls: it is the signer's own device asking,
 * on their own `requestToken`, and the host must not be able to add an
 * address for them. The response carries its own success message, but this
 * call is silent either way — see `InPersonSessionShell`.
 */
export const addInPersonSignerEmail = async (
  requestToken: string,
  requestKey: string,
  signerId: number,
  email: string,
): Promise<void> => {
  try {
    const url = `${urls.IN_PERSON_SIGNER_EMAIL}/${encodeURIComponent(
      requestToken,
    )}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(requestKey)}`;
    const callParams = await getNoAuthCallParams("POST", { signerId, email });
    await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

/** The signer's own e-signing consent. Signing 406s without it. */
export const recordSignerConsent = async (
  requestToken: string,
  requestKey: string,
): Promise<void> => {
  try {
    const callParams = await getNoAuthCallParams("POST", CONSENT_ACCEPTED);
    await makeCall(signerConsentUrl(requestToken, requestKey), callParams);
  } catch (error: any) {
    throw error;
  }
};

/**
 * Who the host may hand the device to on this envelope right now, who it is
 * waiting on when nobody is, and whether a session may start at all.
 *
 * Read before offering a hand-off, and treated as the only authority on
 * readiness: `actionableSigners` is the same list `start` / `next` select
 * from, so a screen that renders only those entries can never target a signer
 * the server would refuse. The screen used to work this out itself by
 * filtering the envelope's signer list, which is what let an email-link
 * recipient sharing a signing order with an in-person one be offered for
 * hand-off.
 *
 * The emailed host link gets the identical payload embedded as `session` on
 * its landing response, so it needs no call of its own.
 */
export const fetchInPersonSessionStatus = async (
  requestId: string,
): Promise<InPersonSessionStatus> => {
  try {
    const url = sessionUrl(urls.IN_PERSON_SESSION_STATUS, requestId);
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Start a session — returns the next actionable in-person signer, or `sessionComplete`. */
export const startInPersonSession = async (
  requestId: string,
  options?: InPersonSessionOptions,
): Promise<InPersonSessionResponse> => {
  try {
    const url = withSignerEmail(
      sessionUrl(urls.IN_PERSON_SESSION_START, requestId),
      options?.signerEmail,
    );
    const callParams = await getCallParams("POST", captureBody(options));
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Advance to the next in-person signer after one finishes. */
export const advanceInPersonSession = async (
  requestId: string,
  options?: InPersonSessionOptions,
): Promise<InPersonSessionResponse> => {
  try {
    const url = withSignerEmail(
      sessionUrl(urls.IN_PERSON_SESSION_NEXT, requestId),
      options?.signerEmail,
    );
    const callParams = await getCallParams("POST", captureBody(options));
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Return the device to the host. `abandoned:true` cancels the current signer
 * without signing — they stay actionable and can be picked up again later —
 * and is what the UI always sends, since every "Return to host" affordance
 * means the signer did not finish. `false` (leave the signer mid-session) is
 * part of the endpoint contract but has no UI path today.
 *
 * Identified by `signerRef`, not email: an in-person signer may have given no
 * email, and without a ref the event cannot be attributed and no audit row is
 * written. (The legacy `signerEmail` field is still accepted as a fallback.)
 *
 * Called when the host ends the hand-off early, which is what clears the
 * backend session lock. The summary screen's "Back to request" does NOT call
 * it: that path is only reached after `next` reported `sessionComplete`, i.e.
 * the backend already closed the session itself. Still works on a session that
 * has passed its 15-minute TTL.
 */
export const returnInPersonSession = async (
  requestId: string,
  signerRef: string,
  abandoned: boolean,
): Promise<void> => {
  try {
    const url = sessionUrl(urls.IN_PERSON_SESSION_RETURN, requestId);
    const callParams = await getCallParams("POST", { signerRef, abandoned });
    await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

/**
 * The public link mailed to `signRequestConfig.inPersonHostEmail`
 * (`/in-person/host?token=&key=`) — mirrors every host-auth call above one
 * for one, `hostToken`-scoped instead of session-scoped so the host runs the
 * whole hand-off without ever logging in. Same request/response shapes
 * throughout; only the credential and the base path differ. See
 * `InPersonHostLinkScreen`.
 *
 * Every `/callback/` route here needs `?id=<hostToken>&key=`, same shape as
 * `signerConsentUrl` / `addInPersonSignerEmail` above — unlike the host-auth
 * calls, which sit under `sign/send/` and don't take either. `key` is the
 * signed token `DocumentSignHelper.inPersonHostLinkUrl` mints alongside
 * `hostToken`, read by `InPersonHostLinkScreen` off its own URL and threaded
 * through (and, once a session starts, persisted in `inPersonSessionSlice`
 * as `hostKey` — see `InPersonSessionShell`, which calls these after the
 * browser has already navigated away from this URL).
 */
const withHostKey = (base: string, hostToken: string, key: string, signerEmail?: string) =>
  `${base}?id=${encodeURIComponent(hostToken)}&key=${encodeURIComponent(key)}${
    signerEmail ? `&signerEmail=${encodeURIComponent(signerEmail)}` : ""
  }`;

/** Resolve the link: envelope summary, whose turn it is, and consent state. */
export const fetchInPersonHostLink = async (
  hostToken: string,
  hostKey: string,
): Promise<InPersonHostLinkResponse> => {
  try {
    const url = withHostKey(
      sessionUrl(urls.IN_PERSON_HOST_LINK, hostToken),
      hostToken,
      hostKey,
    );
    const callParams = getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** The host attestation, posted against the link token instead of a session. */
export const recordInPersonHostLinkConsent = async (
  hostToken: string,
  hostKey: string,
): Promise<void> => {
  try {
    const url = withHostKey(
      sessionUrl(urls.IN_PERSON_HOST_LINK_CONSENT, hostToken),
      hostToken,
      hostKey,
    );
    const callParams = await getNoAuthCallParams("POST", CONSENT_ACCEPTED);
    await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const startInPersonHostLinkSession = async (
  hostToken: string,
  hostKey: string,
  options?: InPersonSessionOptions,
): Promise<InPersonSessionResponse> => {
  try {
    const url = withHostKey(
      sessionUrl(urls.IN_PERSON_HOST_LINK_SESSION_START, hostToken),
      hostToken,
      hostKey,
      options?.signerEmail,
    );
    const callParams = await getNoAuthCallParams("POST", captureBody(options));
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const advanceInPersonHostLinkSession = async (
  hostToken: string,
  hostKey: string,
  options?: InPersonSessionOptions,
): Promise<InPersonSessionResponse> => {
  try {
    const url = withHostKey(
      sessionUrl(urls.IN_PERSON_HOST_LINK_SESSION_NEXT, hostToken),
      hostToken,
      hostKey,
      options?.signerEmail,
    );
    const callParams = await getNoAuthCallParams("POST", captureBody(options));
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const returnInPersonHostLinkSession = async (
  hostToken: string,
  hostKey: string,
  signerRef: string,
  abandoned: boolean,
): Promise<void> => {
  try {
    const url = withHostKey(
      sessionUrl(urls.IN_PERSON_HOST_LINK_SESSION_RETURN, hostToken),
      hostToken,
      hostKey,
    );
    const callParams = await getNoAuthCallParams("POST", {
      signerRef,
      abandoned,
    });
    await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};
